import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import logoImg from '../../components/image/PrrepX Logo.jpg';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { findSeriesById } from '../../data/testSeriesData';
import { doc, getDoc } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref as storageRef, listAll } from 'firebase/storage';
import { calculateTestStatus, parsePurchaseDate } from '../../utils/testStatusHelper';
import './PurchasedTestDetailPage.css';

const PurchasedTestDetailPage = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const navState = (typeof window !== 'undefined' && window.history && window.history.state && window.history.state.usr) ? window.history.state.usr : undefined;
    const { currentUser, loading: authLoading } = useAuth();
    
    const [courseData, setCourseData] = useState(null);
    const [transactionData, setTransactionData] = useState(null);
    const [bookedViaCombo, setBookedViaCombo] = useState(null); // {comboId, comboTitle} if booked via combo
    const [debugCombos, setDebugCombos] = useState([]);
    const [userName, setUserName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [testFolders, setTestFolders] = useState([]);
    const [loadingTests, setLoadingTests] = useState(false);
    const [includedSeries, setIncludedSeries] = useState([]); // For combo packs
    const [comboTestFolders, setComboTestFolders] = useState({}); // {seriesId: [folders]}

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!authLoading && !currentUser) {
            navigate('/login', { state: { from: `/purchased-tests/${courseId}` } });
        }
    }, [currentUser, authLoading, navigate, courseId]);

    useEffect(() => {
        // If combo info is forwarded via navigation, use it as a priority
        const forwardedComboInfo = (navState && navState.comboInfo) ? navState.comboInfo : null;
        const loadCourseData = async () => {
            if (!currentUser || !courseId) {
                navigate('/purchased-tests');
                return;
            }

            const series = findSeriesById(courseId);
            // If combo, redirect to main purchased tests page
            if (series && series.Contains_ids && Array.isArray(series.Contains_ids)) {
                navigate('/purchased-tests');
                return;
            }

            // If forwarded combo info exists, set it immediately
            if (forwardedComboInfo) {
                setBookedViaCombo({ comboId: forwardedComboInfo.comboId, comboTitle: forwardedComboInfo.comboTitle });
            }

            try {
                setLoading(true);
                if (!series) {
                    setError('Course not found');
                    return;
                }

                setCourseData(series);
                setIncludedSeries([]);

                const userDocSnap = await getDoc(doc(db, 'users', currentUser.uid));
                let userNameFinal = '';
                let purchasedCourses = [];
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    userNameFinal = userData.username || userData.displayName || userData.name || '';
                    purchasedCourses = userData.purchasedCourses || [];
                } else {
                    userNameFinal = currentUser.displayName || '';
                }
                setUserName(userNameFinal);

                // If combo info is forwarded, use it to fetch combo transaction directly
                let tx = null;
                let purchasedDate = null;
                let expiryDate = null;
                let validityYears = series.ValidityYears || 1;
                let amount = null;
                let paymentId = null;
                let debugCombosArr = [];

                if (forwardedComboInfo) {
                    // Use combo transaction
                    const comboTxDocId = `${currentUser.uid}_${forwardedComboInfo.comboId}`;
                    const comboTxDocSnap = await getDoc(doc(db, 'transactions', comboTxDocId));
                    if (comboTxDocSnap.exists()) {
                        tx = comboTxDocSnap.data();
                        purchasedDate = parsePurchaseDate(tx);
                        // Individual tests in combo inherit combo's validity
                        const comboSeries = findSeriesById(forwardedComboInfo.comboId);
                        validityYears = comboSeries?.ValidityYears || 2;
                        const { expiryDate: calcExpiry } = calculateTestStatus(purchasedDate, validityYears);
                        expiryDate = calcExpiry;
                        amount = tx.amount;
                        paymentId = tx.paymentId;
                        setBookedViaCombo({ comboId: forwardedComboInfo.comboId, comboTitle: forwardedComboInfo.comboTitle });
                        debugCombosArr.push({comboId: forwardedComboInfo.comboId, comboTitle: forwardedComboInfo.comboTitle, hasTx: true, forwarded: true});
                    } else {
                        // Fallback to normal logic if combo transaction not found
                        setBookedViaCombo(null);
                    }
                }
                if (!tx) {
                    // Try direct transaction first
                    const txDocId = `${currentUser.uid}_${courseId}`;
                    let txDocSnap = await getDoc(doc(db, 'transactions', txDocId));
                    if (txDocSnap.exists()) {
                        tx = txDocSnap.data();
                        purchasedDate = parsePurchaseDate(tx);
                        const { expiryDate: calcExpiry } = calculateTestStatus(purchasedDate, validityYears);
                        expiryDate = calcExpiry;
                        amount = tx.amount;
                        paymentId = tx.paymentId;
                        setBookedViaCombo(null);
                    } else {
                        // Not purchased directly, check all user's purchased combos for a transaction
                        const allCombos = [
                            ...require('../../data/testSeriesData').jeeUltimateCombo,
                            ...require('../../data/testSeriesData').neetUltimatePremium
                        ];
                        let foundCombo = null;
                        for (const combo of allCombos) {
                            if (combo.Contains_ids && combo.Contains_ids.includes(courseId)) {
                                const hasCombo = purchasedCourses && purchasedCourses.includes(combo.id);
                                if (hasCombo) {
                                    const comboTxDocId = `${currentUser.uid}_${combo.id}`;
                                    const comboTxDocSnap = await getDoc(doc(db, 'transactions', comboTxDocId));
                                    debugCombosArr.push({comboId: combo.id, comboTitle: combo.title, hasTx: comboTxDocSnap.exists()});
                                    if (comboTxDocSnap.exists()) {
                                        foundCombo = { combo, comboTx: comboTxDocSnap.data() };
                                        break;
                                    }
                                }
                            }
                        }
                        setDebugCombos(debugCombosArr);
                        if (foundCombo) {
                            tx = foundCombo.comboTx;
                            purchasedDate = parsePurchaseDate(tx);
                            // Individual tests in combo inherit combo's validity
                            validityYears = foundCombo.combo.ValidityYears || 2;
                            const { expiryDate: calcExpiry } = calculateTestStatus(purchasedDate, validityYears);
                            expiryDate = calcExpiry;
                            amount = tx.amount;
                            paymentId = tx.paymentId;
                            setBookedViaCombo({ comboId: foundCombo.combo.id, comboTitle: foundCombo.combo.title });
                        } else {
                            setBookedViaCombo(null);
                        }
                    }
                }

                if (!tx) {
                    setError('You have not purchased this course');
                    return;
                }

                const { isExpired: isExp } = calculateTestStatus(purchasedDate, validityYears);
                setIsExpired(isExp);
                setTransactionData({
                    purchasedDate,
                    expiryDate,
                    amount,
                    paymentId,
                    ...tx
                });

                if (!isExp) {
                    await loadTestFolders(series);
                }

            } catch (err) {
                console.error('Error loading course:', err);
                setError('Error loading course details');
            } finally {
                setLoading(false);
            }
        };

        loadCourseData();
    }, [currentUser, courseId, navigate]);

    // If comboMode is true, return folders instead of setting state
    const loadTestFolders = async (series, comboMode = false) => {
        if (!comboMode) setLoadingTests(true);
        try {
            const category = series?.category || (series.id?.includes('jee') ? 'JEE' : 'NEET');
            const storagePath = `PremiumTestSeries/${category}`;
            const folderRef = storageRef(storage, storagePath);
            const result = await listAll(folderRef);

            const testFoldersList = result.prefixes
                .map(prefix => ({
                    id: prefix.name,
                    name: prefix.name.replace(/_/g, ' '),
                    path: prefix.fullPath,
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            if (comboMode) {
                return testFoldersList;
            } else {
                setTestFolders(testFoldersList);
            }
        } catch (err) {
            console.error('Error loading test folders:', err);
            if (comboMode) {
                return [];
            } else {
                setTestFolders([]);
            }
        } finally {
            if (!comboMode) setLoadingTests(false);
        }
    };

    const handleDownloadInvoice = async () => {
        if (!transactionData) return;
        const pdfDoc = new jsPDF();
        const userNameFinal = userName || currentUser.displayName || 'N/A';
        let inv = null;
        // If booked via combo, fetch combo transaction and details for invoice
        if (bookedViaCombo) {
            // Fetch combo transaction
            const comboSeries = findSeriesById(bookedViaCombo.comboId);
            const comboTxDocId = `${currentUser.uid}_${bookedViaCombo.comboId}`;
            let comboTxDocSnap = await getDoc(doc(db, 'transactions', comboTxDocId));
            let comboTx = comboTxDocSnap.exists() ? comboTxDocSnap.data() : null;
            let comboPurchasedDate = comboTx?.date?.toDate ? comboTx.date.toDate() : (comboTx?.date ? new Date(comboTx.date) : transactionData.purchasedDate);
            inv = {
                id: comboTx?.paymentId || bookedViaCombo.comboId,
                date: { toDate: () => comboPurchasedDate },
                userEmail: currentUser.email,
                userId: currentUser.uid,
                courseTitle: comboSeries?.title || bookedViaCombo.comboTitle,
                amount: comboTx?.amount || transactionData.amount,
                paymentId: comboTx?.paymentId || bookedViaCombo.comboId
            };
        } else {
            // Normal/individual
            inv = {
                id: transactionData.paymentId || courseId,
                date: { toDate: () => transactionData.purchasedDate },
                userEmail: currentUser.email,
                userId: currentUser.uid,
                courseTitle: courseData?.title || '',
                amount: transactionData.amount,
                paymentId: transactionData.paymentId || courseId
            };
        }
        const generatePDF = (logoBase64) => {
            pdfDoc.addImage(logoBase64, 'JPEG', 15, 15, 30, 25);
            pdfDoc.setFontSize(22);
            pdfDoc.setTextColor(30, 58, 138); 
            pdfDoc.text('TAX INVOICE', 195, 25, { align: 'right' });
            pdfDoc.setFontSize(10);
            pdfDoc.setTextColor(100);
            pdfDoc.text(`Invoice No: #INV-${String(inv.id).substring(0,8).toUpperCase()}`, 195, 32, { align: 'right' });
            pdfDoc.text(`Date: ${inv.date?.toDate ? inv.date.toDate().toLocaleDateString() : 'N/A'}`, 195, 37, { align: 'right' });
            pdfDoc.setDrawColor(34, 197, 94);
            pdfDoc.setLineWidth(1);
            pdfDoc.rect(160, 45, 35, 12);
            pdfDoc.setTextColor(34, 197, 94);
            pdfDoc.setFontSize(14);
            pdfDoc.setFont('helvetica', 'bold');
            pdfDoc.text('PAID', 177.5, 53, { align: 'center' });
            pdfDoc.setTextColor(0);
            pdfDoc.setFontSize(11);
            pdfDoc.text('PrrepX Pathshala Private Limited', 15, 55);
            pdfDoc.setFont('helvetica', 'normal');
            pdfDoc.setFontSize(9);
            pdfDoc.text(['CIN: U85306UP2025PTC238181', 'GSTIN: 09AAQCP4420J1Z2', 'Lucknow, UP-226301'], 15, 60);
            pdfDoc.setFont('helvetica', 'bold');
            pdfDoc.text('BILL TO:', 15, 80);
            pdfDoc.setFont('helvetica', 'normal');
            pdfDoc.text([`Name: ${userNameFinal}`, `Email: ${inv.userEmail}`, `User ID: ${inv.userId}`], 15, 85);
            pdfDoc.setFillColor(37, 99, 235);
            pdfDoc.rect(15, 105, 180, 10, 'F');
            pdfDoc.setFont('helvetica', 'bold');
            pdfDoc.setFontSize(11);
            pdfDoc.setTextColor(255);
            pdfDoc.text('Description', 20, 112);
            pdfDoc.text('Qty', 120, 112);
            pdfDoc.text('Unit Price', 140, 112);
            pdfDoc.text('Total', 170, 112);
            pdfDoc.setFillColor(248, 250, 252);
            pdfDoc.rect(15, 115, 180, 10, 'F');
            pdfDoc.setFont('helvetica', 'normal');
            pdfDoc.setFontSize(10);
            pdfDoc.setTextColor(0);
            pdfDoc.text(inv.courseTitle, 20, 122);
            pdfDoc.text('1', 120, 122);
            pdfDoc.text(`INR ${inv.amount}`, 140, 122);
            pdfDoc.text(`INR ${inv.amount}`, 170, 122);
            const finalY = 137;
            pdfDoc.setFontSize(12);
            pdfDoc.setFont('helvetica', 'bold');
            pdfDoc.text(`Total Amount: INR ${inv.amount}`, 195, finalY, { align: 'right' });
            pdfDoc.setFontSize(9);
            pdfDoc.setFont('helvetica', 'normal');
            pdfDoc.text(`Payment ID: ${inv.paymentId}`, 15, finalY);
            pdfDoc.text('This is a computer generated invoice and requires no signature.', 105, 285, { align: 'center' });
            pdfDoc.save(`Invoice_${userNameFinal.replace(/\s+/g, '_')}.pdf`);
        };
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.src = logoImg;
        img.onload = function () {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            generatePDF(canvas.toDataURL('image/jpeg'));
        };
    };

    if (loading) {
        return (
            <div className="pt-loader-container">
                <div className="pt-spinner"></div>
                <p>Loading course details...</p>
            </div>
        );
    }

    return (
        <div className="pt-detail-wrapper">
            <div className="pt-detail-container">
                {/* Header Navigation */}
                <div className="pt-header-nav">
                    <button className="pt-back-link" onClick={() => navigate('/purchased-tests')}>
                        <span>←</span> Back to My Active Tests
                    </button>
                    <h1>Test Dashboard</h1>
                </div>

                <div className="pt-dashboard-grid">
                    {/* LEFT COLUMN: Course Info */}
                    <aside className="pt-left-col">
                        <div className="pt-info-card">
                            <div className="pt-info-header">
                                <h2>{courseData?.title}</h2>
                                <span className={`pt-badge ${isExpired ? 'is-expired' : 'is-active'}`}>
                                    {isExpired ? '⏱ Expired' : '✓ Active'}
                                </span>
                                {bookedViaCombo && (
                                    <span className="pt-badge pt-badge-combo">Booked via Combo: {bookedViaCombo.comboTitle}</span>
                                )}
                            </div>

                            <div className="pt-info-body">
                                <div className="pt-meta-section">
                                    <h3>📋 What's Included:</h3>
                                    {includedSeries.length > 0 ? (
                                        <div className="pt-combo-main-grid">
                                            <h3 className="pt-combo-title">Combo Pack: {courseData?.title}</h3>
                                            <div className="pt-combo-subgrid-wrapper">
                                                {includedSeries.map((s, idx) => (
                                                    <div key={s.id} className="pt-combo-subgrid">
                                                        <div className="pt-combo-header">
                                                            <span className="pt-combo-individual-title">{s.title}</span>
                                                        </div>
                                                        <ul className="pt-combo-benefits">
                                                            {s.benefits?.slice(0, 3).map((b, i) => (
                                                                <li key={i}>✓ {b}</li>
                                                            ))}
                                                        </ul>
                                                        <div className="pt-combo-access">
                                                            <span className="pt-combo-access-label">Access:</span>
                                                            <button
                                                                className="pt-btn-start pt-combo-access-btn"
                                                                disabled={isExpired}
                                                                onClick={() => navigate(`/premium-test/${s.id}`)}
                                                            >
                                                                {isExpired ? 'Expired' : 'Open Test'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <ul className="pt-list">
                                            {courseData?.benefits?.map((benefit, idx) => (
                                                <li key={idx}>✓ {benefit}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="pt-meta-section">
                                    <h3>📅 Subscription Details:</h3>
                                    <div className="pt-data-grid">
                                        <div className="pt-data-box">
                                            <label>Purchased On</label>
                                            <p>{transactionData?.purchasedDate.toLocaleDateString()}</p>
                                        </div>
                                        <div className="pt-data-box">
                                            <label>Expires On</label>
                                            <p>{transactionData?.expiryDate.toLocaleDateString()}</p>
                                        </div>
                                        <div className="pt-data-box">
                                            <label>Amount Paid</label>
                                            <p className="pt-highlight">₹{transactionData?.amount}</p>
                                        </div>
                                        {transactionData?.paymentId && (
                                            <div className="pt-data-box full">
                                                <label>Payment ID</label>
                                                <code className="pt-code">{transactionData.paymentId}</code>
                                            </div>
                                        )}
                                    </div>
                                    <button className="pt-btn-invoice" onClick={handleDownloadInvoice}>
                                        Download Invoice PDF
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Status Alert Box */}
                        <div className={`pt-status-alert ${isExpired ? 'alert-danger' : 'alert-success'}`}>
                            {isExpired ? (
                                <>
                                    <h4>⏰ Your subscription has expired</h4>
                                    <p>Please renew to continue learning.</p>
                                    <button className="pt-btn-renew" onClick={() => navigate('/test-series')}>Renew Now</button>
                                </>
                            ) : (
                                <>
                                    <h4>✅ Subscription Active</h4>
                                    <p>You have full access until {transactionData?.expiryDate.toLocaleDateString()}</p>
                                </>
                            )}
                        </div>

                        {/* Instructions */}
                        <div className="pt-usage-card">
                            <h3>ℹ️ How to Use</h3>
                            <ol>
                                <li>Click "Start Test" to begin attempting a test series</li>
                                <li>Each test is timed and includes detailed solutions</li>
                                <li>Your results are automatically saved to your profile</li>
                                <li>Track your progress and performance over time</li>
                            </ol>
                        </div>
                    </aside>

                    {/* RIGHT COLUMN: Tests List */}
                    <main className="pt-right-col">
                        {!isExpired && (
                            <section className="pt-tests-section">
                                <div className="pt-section-title">
                                    <h2>📚 Available Tests</h2>
                                    {includedSeries.length > 0 ? null : (
                                        <span className="pt-count">{testFolders.length} Tests found</span>
                                    )}
                                </div>

                                {loadingTests ? (
                                    <div className="pt-mini-loader">Loading content...</div>
                                ) : includedSeries.length > 0 ? (
                                    <div className="pt-combo-main-grid">
                                        <h3 className="pt-combo-title">Combo Pack: {courseData?.title}</h3>
                                        <div className="pt-combo-subgrid-wrapper">
                                            {includedSeries.map((s) => (
                                                <div key={s.id} className="pt-combo-subgrid">
                                                    <div className="pt-combo-header">
                                                        <span className="pt-combo-individual-title">{s.title}</span>
                                                    </div>
                                                    {comboTestFolders[s.id]?.length > 0 ? (
                                                        <div className="pt-test-stack">
                                                            {comboTestFolders[s.id].map((test) => (
                                                                <div key={test.id} className="pt-test-item">
                                                                    <div className="pt-test-info">
                                                                        <h4>{test.name}</h4>
                                                                        <p>Complete test series to evaluate your preparation</p>
                                                                    </div>
                                                                    <button
                                                                        className="pt-btn-start"
                                                                        disabled={isExpired}
                                                                        onClick={() => navigate(`/premium-test/${s.id}/${test.id}`)}
                                                                    >
                                                                        {isExpired ? 'Expired' : '🚀 Start Test'}
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="pt-empty">
                                                            <p>No tests available yet for this series. Content will be available soon!</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : testFolders.length > 0 ? (
                                    <div className="pt-test-stack">
                                        {testFolders.map((test) => (
                                            <div key={test.id} className="pt-test-item">
                                                <div className="pt-test-info">
                                                    <h3>{test.name}</h3>
                                                    <p>Complete test series to evaluate your preparation</p>
                                                </div>
                                                <button
                                                    className="pt-btn-start"
                                                    onClick={() => navigate(`/premium-test/${courseId}/${test.id}`)}
                                                >
                                                    🚀 Start Test
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="pt-empty">
                                        <p>No tests available yet. Content will be available soon!</p>
                                    </div>
                                )}
                            </section>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default PurchasedTestDetailPage;